import { ColDef, GridOptions } from 'ag-grid-community'
import { OrganisationMetaDetail } from 'api'
import {
  TypedValueGetterParams,
  gridOptionsBase,
  groupingRowColDef,
  StandardGridColumnId,
  withAllContext,
  createGroupedActionRenderer,
  createActionsRenderer,
  ValidationCellRenderer
} from 'view/components/datagrid'
import { ResponderSalesCoverageActionsButton } from './ResponderSalesCoverageActionsButton'
import { Locale, Translation } from 'locale'
import { UserSelectbox } from 'view/shared/entity/UserSelectbox'
import { UserHelper } from 'model'

enum SalesCoverageColumnId {
  MY_FIRM = 'MY_FIRM',
  MY_ENTITY = 'MY_ENTITY',
  COUNTERPARTY_FIRM = 'COUNTERPARTY_FIRM',
  COUNTERPARTY = 'COUNTERPARTY',
  PRODUCT = 'PRODUCT',
  SECTOR = 'SECTOR',
  SPLIT_TRADES = 'SPLIT_TRADES',
  SALES_COVER_PRIMARY_COVER_USER = 'SALES_COVER_PRIMARY_COVER_USER',
  SALES_COVER_SECONDARY_COVER_USER = 'SALES_COVER_SECONDARY_COVER_USER'
}

export enum ResponderSalesCoverageUpdateFields {
  PRIMARY_COVER_USER_ID = 'organisationMeta.primaryCoverUserId',
  SECONDARY_COVER_USER_ID = 'organisationMeta.secondaryCoverUserId'
}

export const ResponderSalesCoverageColumnDefs: ColDef[] = [
  {
    colId: StandardGridColumnId.CHECKBOX,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    headerCheckboxSelection: true,
    checkboxSelection: true,
    width: 36,
    maxWidth: 36,
    pinned: 'left',
    lockPinned: true,
    lockPosition: true,
    lockVisible: true,
    enableRowGroup: false,
    filter: false,
    resizable: false,
    sortable: false
  },
  groupingRowColDef,
  {
    colId: StandardGridColumnId.ACTIONS,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    equals: () => false,
    width: 150,
    minWidth: 150,
    cellClass: 'action-cell',
    headerComponent: withAllContext(createGroupedActionRenderer(ResponderSalesCoverageActionsButton)),
    cellRenderer: withAllContext(createActionsRenderer(ResponderSalesCoverageActionsButton)),
    pinned: 'left',
    lockPinned: true,
    lockPosition: true,
    lockVisible: true,
    enableRowGroup: false,
    filter: false,
    sortable: false
  },
  {
    colId: SalesCoverageColumnId.MY_FIRM,
    headerName: Locale.translate(Translation.TC_MY_FIRM),
    valueGetter: (params: TypedValueGetterParams<OrganisationMetaDetail>) => params.data?.ownerOrganisation?.legalName,
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: SalesCoverageColumnId.MY_ENTITY,
    headerName: Locale.translate(Translation.TC_MY_ENTITY),
    valueGetter: (params: TypedValueGetterParams<OrganisationMetaDetail>) =>
      params.data?.ownerTradingEntity?.publishedName ?? Locale.translate(Translation.TC_ORGANISATION_META_ALL_VALUES),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: SalesCoverageColumnId.COUNTERPARTY_FIRM,
    headerName: Locale.translate(Translation.TC_COUNTERPARTY_FIRM),
    valueGetter: (params: TypedValueGetterParams<OrganisationMetaDetail>) =>
      params.data?.subjectOrganisation?.legalName ?? Locale.translate(Translation.TC_ORGANISATION_META_ALL_VALUES),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: SalesCoverageColumnId.COUNTERPARTY,
    headerName: Locale.translate(Translation.TC_COUNTERPARTY),
    valueGetter: (params: TypedValueGetterParams<OrganisationMetaDetail>) =>
      params.data?.subjectTradingEntity?.publishedName ?? Locale.translate(Translation.TC_ORGANISATION_META_ALL_VALUES),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: SalesCoverageColumnId.PRODUCT,
    headerName: Locale.translate(Translation.TC_PRODUCT),
    valueGetter: (params: TypedValueGetterParams<OrganisationMetaDetail>) =>
      params.data?.product?.name ?? Locale.translate(Translation.TC_ORGANISATION_META_ALL_VALUES),
    width: 140,
    filter: 'agSetColumnFilter'
  },
  {
    colId: SalesCoverageColumnId.SECTOR,
    headerName: Locale.translate(Translation.TC_SECTOR),
    valueGetter: (params: TypedValueGetterParams<OrganisationMetaDetail>) =>
      params.data?.productSector?.name ?? Locale.translate(Translation.TC_ORGANISATION_META_ALL_VALUES),
    width: 140,
    filter: 'agSetColumnFilter'
  },
  {
    colId: SalesCoverageColumnId.SALES_COVER_PRIMARY_COVER_USER,
    headerName: Locale.translate(Translation.TC_PRIMARY_COVER),
    valueGetter: (params: TypedValueGetterParams<OrganisationMetaDetail>) => {
      const user = params.data?.primaryCoverUser
      return user ? UserHelper.getName(user) : null
    },
    width: 250,
    filter: 'agSetColumnFilter',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellEditorParams: {
      inputComponent: UserSelectbox,
      updateField: ResponderSalesCoverageUpdateFields.PRIMARY_COVER_USER_ID,
      inputParams: (orgMeta: OrganisationMetaDetail) => ({ canClear: !!orgMeta.organisationMeta.secondaryCoverUserId })
    },
    editable: true
  },
  {
    colId: SalesCoverageColumnId.SALES_COVER_SECONDARY_COVER_USER,
    headerName: Locale.translate(Translation.TC_SECONDARY_COVER),
    valueGetter: (params: TypedValueGetterParams<OrganisationMetaDetail>) => {
      const user = params.data?.secondaryCoverUser
      return user ? UserHelper.getName(user) : null
    },
    width: 250,
    filter: 'agSetColumnFilter',
    equals: () => false,
    cellRenderer: ValidationCellRenderer,
    cellEditorParams: {
      inputComponent: UserSelectbox,
      updateField: ResponderSalesCoverageUpdateFields.SECONDARY_COVER_USER_ID,
      inputParams: (orgMeta: OrganisationMetaDetail) => ({ canClear: !!orgMeta.organisationMeta.primaryCoverUserId })
    },
    editable: true
  }
]

export const responderSalesCoverageGridOptions: GridOptions = {
  ...gridOptionsBase,
  getRowId: ({ data }) => data.organisationMeta.id
}
